import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Dropdown, Select, Switch } from 'antd';
import { Icon as CroplerIcon, Button } from '@cropler/ui-kit';
import dayjs from 'dayjs';

import styles from './styles.module.scss';
import stylesDropdownMenu from '../../styles/dropdownMenu.module.scss';

import { selectDate, selectPhotosTime } from '../../store/slices/sensors';
import { DateFormat, DateLLFormat } from '../../constants/data';
import { useViewport } from '../../hooks';
import { BreakpointDesktop, BreakpointMobile, BreakpointMobileUp, BreakpointTablet } from '../../constants/ui';
import { downloadPhotoByUrl, shareImageMobileApp, shareImageWeb } from '../../utils/images';
import { AiAnalyticsLabel } from '../AiAnalytics/index.jsx';
import { ImageZoomModals } from '../ImageZoomModals/index.jsx';
import { ApplyMask, LoadImage, ResolveImageSrc } from './lib/index.jsx';

const ImageBox = memo(
  ({
    t,
    lastDate,
    firstDate,
    dates = [],
    isVegetation = false,
    onClickDownloadImages,
    changeTime,
    sensor = { id: '', photos: { ndvi: [], day: [], night: [] }, phenostage: t('sensor.noStageAdded') },
  }) => {
    const { selectedDate, photosType, photosTime } = useSelector((state) => state.sensors);
    const { currentUserRole } = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const { widthViewport } = useViewport();

    const isMobileApp = Capacitor.isNativePlatform();

    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    const [mode, setMode] = useState(isVegetation && photosType === 'day' ? 'ndvi' : photosType);
    const [modalOpen, setModalOpen] = useState(false);
    const [time, setTime] = useState('');
    const [isMask, setIsMask] = useState(true);
    const [isNdvi, setIsNdvi] = useState(false);
    const [img, setImg] = useState('');
    const [processedMaskUrl, setProcessedMaskUrl] = useState(null);
    const maskCacheRef = useRef({});
    const lastShownImgRef = useRef({ url: '', maskUrl: null });

    const onClickDownloadPhoto = async () => {
      if (!img || !img.url) return;

      const shouldDownloadWithMask = isVegetation && isMask && !!processedMaskUrl;

      // with mask
      if (!shouldDownloadWithMask) {
        await downloadPhotoByUrl({ url: img.url, urlName: img.url, takenAt: img.taken_at, isWithMask: false });
        return;
      }

      // without mask
      try {
        const [baseImage, overlayMask] = await Promise.all([LoadImage(img.url), LoadImage(processedMaskUrl)]);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = baseImage.width;
        canvas.height = baseImage.height;

        ctx.drawImage(baseImage, 0, 0);
        ctx.drawImage(overlayMask, 0, 0, canvas.width, canvas.height);

        const mergedImageUrl = canvas.toDataURL('image/jpeg', 0.95);
        await downloadPhotoByUrl({
          url: mergedImageUrl,
          urlName: img.url,
          takenAt: img.taken_at,
          isWithMask: true,
        });
      } catch (error) {
        console.error('Failed to prepare masked image download:', error);
        await downloadPhotoByUrl({ url: img.url, urlName: img.url, takenAt: img.taken_at });
      }
    };

    const menu = [
      {
        key: '1',
        label: (
          <div className={stylesDropdownMenu.dropdownMenuItem}>
            <Button
              type="text"
              className={stylesDropdownMenu.dropdownMenuBtn}
              icon={<CroplerIcon name="download" />}
              onClick={onClickDownloadPhoto}
            >
              {t('sensor.downloadPhoto')}
            </Button>
          </div>
        ),
      },
      {
        key: '2',
        label: (
          <div className={stylesDropdownMenu.dropdownMenuItem}>
            <Button
              type="text"
              className={stylesDropdownMenu.dropdownMenuBtn}
              icon={<CroplerIcon name="archive" />}
              onClick={() => onClickDownloadImages(mode)}
            >
              {t('sensor.downloadAllPhotos')}
            </Button>
          </div>
        ),
      },
    ];

    const images = useMemo(
      () =>
        sensor
          ? {
              ndvi: sensor.photos && sensor.photos.ndvi.length > 0 ? sensor.photos.ndvi : null,
              day: sensor.photos && sensor.photos.day.length > 0 ? sensor.photos.day : null,
              night: sensor.photos && sensor.photos.night.length > 0 ? sensor.photos.night : null,
              mask: sensor.photos && sensor.photos.mask && sensor.photos.mask.length > 0 ? sensor.photos.mask : null,
            }
          : { ndvi: null, day: null, night: null, mask: null },
      [sensor],
    );

    const sortedDayNightImages = useMemo(() => {
      const parseTime = (takenAt) => dayjs(takenAt, ['hh:mm A', 'HH:mm']).valueOf();
      const dayItems = (images.day || []).map((p) => ({ ...p, mode: 'day' }));
      const nightItems = (images.night || []).map((p) => ({ ...p, mode: 'night' }));
      return [...dayItems, ...nightItems].sort((a, b) => parseTime(a.taken_at) - parseTime(b.taken_at));
    }, [images.day, images.night, isNdvi]);

    const noImage = useMemo(
      () =>
        !images[mode] ||
        !time ||
        !images[mode].find((p) => p.taken_at === time) ||
        images[mode].find((p) => p.taken_at === time && !p.url),
      [images, mode, time],
    );

    const onSwitchMode = ({ mode, time }) => {
      setTime(time);
      setMode(mode);
      changeTime(time);
      dispatch(selectPhotosTime(time));
    };

    const handleTouchStart = (e) => {
      setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e) => {
      setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
      if (widthViewport > BreakpointTablet || !touchStart || !touchEnd) return;

      const distance = touchStart - touchEnd;
      const minSwipeDistance = 50; // Define your minimum distance for a swipe

      if (distance > minSwipeDistance) {
        // Swiped Left
        const nextDate = dayjs(selectedDate).add(1, 'day').toString();
        if (!(dayjs(lastDate) < dayjs(nextDate))) {
          dispatch(selectDate(dayjs(nextDate).format(DateFormat)));
        }
      } else if (distance < -minSwipeDistance) {
        // Swiped Right
        const prevDate = dayjs(selectedDate).subtract(1, 'day').toString();
        if (!(dayjs(prevDate) < dayjs(firstDate))) {
          dispatch(selectDate(dayjs(prevDate).format(DateFormat)));
        }
      }
    };

    const shareTitle = () =>
      `${sensor.location.name} location, ${dayjs(sensor.date).format(DateLLFormat)} ${time}` || 'Cropler';

    // Build the image to share, merging the vegetation mask onto the photo when enabled.
    const getShareImage = async () => {
      const shouldShareWithMask = isVegetation && isMask && !!processedMaskUrl;

      // without mask
      if (!shouldShareWithMask) {
        return { url: img.url, fileName: undefined };
      }

      // with mask
      try {
        // On native (capacitor://localhost) a remote photo taints the canvas and breaks
        // toDataURL, so resolve it to a same-origin data URL first (no-op on web).
        const resolvedBaseSrc = await ResolveImageSrc(img.url);
        const [baseImage, overlayMask] = await Promise.all([LoadImage(resolvedBaseSrc), LoadImage(processedMaskUrl)]);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = baseImage.width;
        canvas.height = baseImage.height;

        ctx.drawImage(baseImage, 0, 0);
        ctx.drawImage(overlayMask, 0, 0, canvas.width, canvas.height);

        const url = canvas.toDataURL('image/jpeg', 0.95);

        const takenAt = img.taken_at ?? '';
        let baseName = img.url.split('/').pop()?.split('?')[0] || 'image.jpg';
        baseName = baseName.replace(/T[^-]+-/, `T${takenAt.replace(/[: ]/g, '-')}-`);
        return { url, fileName: `vegetation-${baseName}` };
      } catch (error) {
        console.error('Failed to prepare masked image share:', error);
        return { url: img.url, fileName: undefined };
      }
    };

    const onShare = async () => {
      if (!img || !img.url) return;

      const { url, fileName } = await getShareImage();
      const shared = await shareImageWeb(url, shareTitle(), fileName);
      if (!shared && !navigator.share) {
        alert(t('sensor.downloadPhotoError'));
      }
    };

    const onShareMobile = async () => {
      if (!img || !img.url) return;

      const { url, fileName } = await getShareImage();
      await shareImageMobileApp(url, shareTitle(), fileName);
    };

    const onOpenFullImageModal = (status) => {
      setModalOpen(status);
      if (status && widthViewport < BreakpointMobileUp) {
        document.body.classList.add('overflow-hidden');
      } else {
        document.body.classList.remove('overflow-hidden');
      }
    };

    const onSwitchNdvi = (value) => {
      setIsNdvi(value);
    };

    const currentDateIndex = useMemo(
      () => (dates && dates.length ? dates.findIndex((d) => d === selectedDate) : -1),
      [dates, selectedDate],
    );
    const hasPrevDate = currentDateIndex > 0;
    const hasNextDate = currentDateIndex >= 0 && currentDateIndex < dates.length - 1;

    const goToDate = (date) => {
      if (!date) return;
      dispatch(selectDate(date));
      searchParams.set('date', date);
      setSearchParams(searchParams, { replace: true });
    };

    const onPrevDate = () => {
      if (!hasPrevDate) return;
      goToDate(dates[currentDateIndex - 1]);
    };

    const onNextDate = () => {
      if (!hasNextDate) return;
      goToDate(dates[currentDateIndex + 1]);
    };

    // Switch mask
    const onChangeMask = (checked) => {
      setIsMask(checked);
    };

    const onChangeTime = (value) => {
      setTime(value);
      changeTime(value);
    };

    // Select default time
    useEffect(() => {
      if (!images || !sensor || !sensor.photos || sensor.photos.length < 1) return;
      let currentMode = mode || 'day';

      // images[mode] is null or []
      if (!images[mode] && !isVegetation) {
        currentMode = images['day'] ? 'day' : 'night';
        setMode(currentMode);
      }

      if (!sensor.photos[currentMode] || sensor.photos[currentMode].length === 0) return;

      const photos = sensor.photos[currentMode];
      const parseTime = (t) => dayjs(t, ['hh:mm A', 'HH:mm']).valueOf();

      let timeValue;
      if (photosTime && !isVegetation) {
        const target = parseTime(photosTime);
        timeValue = photos.reduce((closest, p) =>
          Math.abs(parseTime(p.taken_at) - target) < Math.abs(parseTime(closest.taken_at) - target) ? p : closest,
        ).taken_at;
      } else {
        timeValue = isVegetation ? photos.at(-1).taken_at : photos[0].taken_at;
      }

      setTime(timeValue);
      changeTime(timeValue);
    }, [images, sensor]);

    // Get img
    useEffect(() => {
      if (!images || !time || !mode || !sensor || !images[mode]) return;
      const selectedMode = isNdvi && images.ndvi.find((p) => p.taken_at === time) ? 'ndvi' : mode;
      const filteredImages = images[selectedMode].filter((p) => p.taken_at === time);
      setImg(filteredImages ? filteredImages[0] : '');
    }, [images, time, mode, sensor, isNdvi]);

    // Cache last shown image url so the zoom modal does not flicker while next photo is loading
    useEffect(() => {
      if (!noImage && img && img.url) {
        lastShownImgRef.current = { url: img.url, maskUrl: processedMaskUrl };
      }
    }, [img, processedMaskUrl, noImage]);

    // Mask
    useEffect(() => {
      if (!isVegetation || !isMask || !images.mask || !time || !images.mask.find((p) => p.taken_at === time)) {
        setProcessedMaskUrl(null);
        return;
      }
      const maskSrcList = images.mask.filter((p) => p.taken_at === time);
      const maskSrc = maskSrcList && maskSrcList[0] ? maskSrcList[0].url : null;

      if (!maskSrc) {
        setProcessedMaskUrl(null);
        return;
      }
      if (maskCacheRef.current[maskSrc]) {
        setProcessedMaskUrl(maskCacheRef.current[maskSrc]);
        return;
      }
      let cancelled = false;
      ApplyMask(maskSrc)
        .then((dataUrl) => {
          if (!cancelled) {
            maskCacheRef.current[maskSrc] = dataUrl;
            setProcessedMaskUrl(dataUrl);
          }
        })
        .catch((err) => console.error('ApplyMask error:', err));
      return () => {
        cancelled = true;
      };
    }, [isMask, images.mask, mode, time]);

    return (
      <>
        <div className={styles.imageBoxContainer}>
          <div
            className={styles.imageBoxImage}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onClick={() => onOpenFullImageModal(true)}
            role="button"
            style={{
              backgroundImage: !noImage && img && img.url ? `url("${img.url}")` : 'none',
              backgroundSize: noImage ? 'auto' : 'cover',
            }}
          >
            {/*Mask*/}
            {isVegetation && isMask && images.mask && images.mask.length > 0 && processedMaskUrl && (
              <img className={styles.imageBoxMask} src={processedMaskUrl} alt="" />
            )}
            {/*No photo*/}
            {(!images[mode] || images[mode].length < 1) && (
              <CroplerIcon className={styles.imageBoxEmpty} name="empty" />
            )}
          </div>

          <div className={styles.imageBoxHeader}>
            <div className={`${styles.imageBoxHeaderItem} ${isVegetation ? styles.imageBoxHeaderItemVegetation : ''}`}>
              {sensor && isVegetation && images && images[mode] && images[mode].length > 0 && (
                <Select
                  rootClassName={'select-time select-dark-transparent'}
                  suffixIcon={<CroplerIcon name="down" />}
                  size={'large'}
                  value={time}
                  onChange={(value) => onChangeTime(value)}
                  options={images[mode].map((item) => ({
                    value: item.taken_at,
                    label: item.taken_at,
                  }))}
                />
              )}

              {/*Timeline tab*/}
              {!isVegetation && (
                <>
                  {/*Time navigation*/}
                  {sortedDayNightImages && (
                    <div className={styles.imageBoxSwitcher}>
                      {sortedDayNightImages.map((p, index) => (
                        <button
                          key={index}
                          className={`${styles.imageBoxSwitcherButton} ${p.taken_at === time && styles.imageBoxSwitcherButtonActive}`}
                          onClick={() => onSwitchMode({ mode: p.mode, time: p.taken_at })}
                        >
                          {p.mode === 'day' ? (
                            <CroplerIcon name="day" width={16} />
                          ) : (
                            <CroplerIcon name="night" width={14} />
                          )}
                          {p.taken_at}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* NDVI */}
                  {images.ndvi && images.ndvi.length > 0 && (
                    <div className={styles.imageBoxSwitch}>
                      <Switch
                        rootClassName={'switch-dark'}
                        defaultChecked={isNdvi}
                        disabled={!images.ndvi.find((p) => p.taken_at === time)}
                        onChange={onSwitchNdvi}
                      />
                      <span>NDVI</span>
                    </div>
                  )}
                </>
              )}

              {/*Vegetation tab*/}
              {isVegetation && (
                <>
                  <div className={styles.imageBoxSwitcher}>
                    {/* NDVI */}
                    <button
                      className={`${styles.imageBoxSwitcherButton} ${mode === 'ndvi' && styles.imageBoxSwitcherButtonActive}`}
                      onClick={() => setMode('ndvi')}
                    >
                      NDVI
                    </button>
                    {/* Night */}
                    <button
                      className={`${styles.imageBoxSwitcherButton} ${mode === 'night' && styles.imageBoxSwitcherButtonActive}`}
                      onClick={() => setMode('night')}
                    >
                      <CroplerIcon name="night" height={14} />
                      RGB
                    </button>
                  </div>

                  {images.mask && images.mask.length > 0 && (
                    <div className={styles.imageBoxSwitch}>
                      <Switch rootClassName={'switch-dark'} defaultChecked={isMask} onChange={onChangeMask} />
                      Mask
                    </div>
                  )}
                </>
              )}
            </div>

            {!noImage && (
              <div className={styles.imageBoxButtons}>
                {widthViewport < BreakpointMobileUp && !isMobileApp && (
                  <Button type="primary" onClick={onShare} icon={<CroplerIcon name="share" />} />
                )}
                {widthViewport > BreakpointMobile &&
                  !isVegetation &&
                  (currentUserRole === 'owner' || currentUserRole === 'editor') && (
                    <Dropdown menu={{ items: menu }} placement="bottomRight" rootClassName={styles.imageBoxDropdown}>
                      <span>
                        <Button type={'primary'} icon={<CroplerIcon name="more" />} rootClassName={'dropdown-nav'} />
                      </span>
                    </Dropdown>
                  )}
                {widthViewport > BreakpointMobile &&
                  ((currentUserRole !== 'owner' && currentUserRole !== 'editor') || isVegetation) && (
                    <Button type="primary" icon={<CroplerIcon name="download" />} onClick={onClickDownloadPhoto} />
                  )}
                {isMobileApp && (
                  <Button type="primary" icon={<CroplerIcon name="share" />} onClick={onShareMobile} />
                )}
                {/*Show full image*/}
                <Button
                  type="primary"
                  onClick={() => onOpenFullImageModal(true)}
                  icon={<CroplerIcon name="expand" />}
                />
              </div>
            )}
          </div>

          <div className={styles.imageBoxRow}>
            {/*BBCH*/}
            {sensor && sensor.bbchAI && sensor.bbchAI.value && (
              <AiAnalyticsLabel
                text={`BBCH ${sensor.bbchAI.value}`}
                description={sensor.bbchAI.description}
                placement={widthViewport > BreakpointMobile ? 'topLeft' : 'bottomLeft'}
                trigger={widthViewport > BreakpointDesktop ? 'hover' : 'click'}
                hideTooltip={false}
              />
            )}

            {/*Location Name*/}
            {widthViewport > BreakpointMobile && sensor && sensor.location && sensor.location.name && (
              <p className={styles.imageBoxText}>{sensor.location.name}</p>
            )}
          </div>
        </div>

        {/*Full image modal*/}
        {(() => {
          const hasCurrent = !noImage && img && img.url;
          const displayedUrl = hasCurrent ? img.url : modalOpen ? lastShownImgRef.current.url : null;
          const displayedMaskUrl = hasCurrent ? processedMaskUrl : lastShownImgRef.current.maskUrl;
          if (!displayedUrl) return null;
          return (
            <ImageZoomModals
              fullImage={displayedUrl}
              modalOpen={modalOpen}
              noImage={false}
              onCloseModal={() => onOpenFullImageModal(false)}
              isMask={isMask}
              processedMaskUrl={displayedMaskUrl}
              onPrevDate={!isVegetation ? onPrevDate : undefined}
              onNextDate={!isVegetation ? onNextDate : undefined}
              hasPrevDate={hasPrevDate}
              hasNextDate={hasNextDate}
            />
          );
        })()}
      </>
    );
  },
);

ImageBox.displayName = 'ImageBox';
export default ImageBox;
